import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import Footer from '../../components/Footer';
import { 
  Car, 
  MapPin, 
  Clock, 
  Shield, 
  Star, 
  CreditCard,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

export default function RideHailingPage() {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
                alt="Mercapoli Move"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-[#00BFA6]">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-6 py-2 text-sm">
                  Registrarse
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#00BFA6]/5 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00BFA6]/10 rounded-full mb-6">
                <Car className="w-5 h-5 text-[#00BFA6]" />
                <span className="text-[#00BFA6] font-medium text-sm">Ride-Hailing</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-['Outfit'] leading-tight">
                Viajes Inteligentes con 
                <span className="text-[#00BFA6]"> Mercapoli Move</span>
              </h1>
              <p className="text-lg text-slate-600 mt-6 leading-relaxed">
                Mercapoli Move ofrece un servicio de transporte moderno, seguro y eficiente, 
                diseñado para moverte cuando y donde lo necesites.
              </p>
              <p className="text-slate-600 mt-4">
                Solicita viajes en tiempo real, visualiza conductores cercanos, conoce el precio 
                antes de confirmar y paga de forma digital con moneda local o Digi Wallet.
              </p>
              <Link to="/register">
                <Button className="btn-primary mt-8 h-14 px-8 text-lg">
                  Solicitar un viaje ahora
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&h=400&fit=crop" 
                alt="Ride-Hailing"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-12">
            Características Principales
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00BFA6]/10 rounded-xl flex items-center justify-center mb-4">
                <Clock className="w-7 h-7 text-[#00BFA6]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Viajes bajo demanda</h3>
              <p className="text-slate-600">Solicita un viaje en segundos y un conductor llegará a tu ubicación.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#FFD166]/10 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-7 h-7 text-[#FFD166]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Seguimiento en tiempo real</h3>
              <p className="text-slate-600">Visualiza la ubicación exacta de tu conductor y el tiempo estimado de llegada.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00BFA6]/10 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-[#00BFA6]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Conductores verificados</h3>
              <p className="text-slate-600">Todos nuestros conductores pasan por un proceso de verificación riguroso.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#FFD166]/10 rounded-xl flex items-center justify-center mb-4">
                <Star className="w-7 h-7 text-[#FFD166]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Calificaciones bidireccionales</h3>
              <p className="text-slate-600">Califica tu experiencia y conoce la reputación de tu conductor.</p>
            </div>
            <div className="bg-slate-50 p-6 rounded-2xl">
              <div className="w-14 h-14 bg-[#00BFA6]/10 rounded-xl flex items-center justify-center mb-4">
                <CreditCard className="w-7 h-7 text-[#00BFA6]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Pagos digitales</h3>
              <p className="text-slate-600">Paga sin efectivo usando Digi Wallet, tarjeta o tokens DIGGII.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-[#00BFA6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
            ¿Listo para moverte?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Descarga la app y solicita tu primer viaje en minutos.
          </p>
          <Link to="/register">
            <Button className="bg-white text-[#00BFA6] hover:bg-white/90 h-14 px-8 text-lg rounded-full">
              Comenzar Ahora
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
